import React, { useEffect, useState } from 'react';
import { Grid, Stack, Typography } from '@mui/material';
import { AuditLogEventName } from 'pages/AuditLogPage/AuditLogViewPage/styles';
import { IAuditLogItemResponse } from 'models/auditLog';
import { DetailsTemplate, EDetailsPageTypes } from 'components/templates/DetailsTemplate';
import { useTranslations } from 'hooks/useTranslations';
import { EPermission } from 'models/permissions';
import { usePermission } from 'hooks/usePermission';
import { formatDate } from 'utils/date';
import { Chips, EChipsVariants } from 'components/atoms/Chips';
import { IParsedDictionary, parseDictionary } from 'helpers/dictionary';
import { useDictionary } from 'models/dictionaryContext';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { IUser } from 'models/user';
import { getRoles } from 'requests/role';
import { useUsers } from 'pages/UsersPage/hooks';
import { EAuditLogEvents } from 'pages/AuditLogPage/helpers';
import { ERouteLinks } from 'models/route';
import { useNavigate } from 'react-router-dom';
import { useWeaponLicenceModule } from 'hooks/useWeaponLicenceModule';

interface IAdministrationUserEventDetails {
  auditLog: IAuditLogItemResponse;
}

const AdministrationUserEventDetails = ({ auditLog }: IAdministrationUserEventDetails) => {
  const [userDetails, setUserDetails] = useState<IUser | null>();
  const [previousUserDetails, setPreviousUserDetails] = useState<IUser | null>();
  const navigate = useNavigate();
  const [roleNames, setRoleNames] = useState<string[]>([]);
  const [previousRoleNames, setPreviousRoleNames] = useState<string[]>([]);
  const [prefixNames, setPrefixNames] = useState<string[]>([]);
  const [orgCategoryNames, setOrgCategoryNames] = useState<string[]>([]);
  const [weaponLicencingAuthorityNames, setWeaponLicencingAuthorityNames] = useState<string[]>(
    [],
  );
  const [previousPrefixNames, setPreviousPrefixNames] = useState<string[]>([]);
  const [previousOrgCategoryNames, setPreviousOrgCategoryNames] = useState<string[]>([]);
  const [previousWeaponLicencingAuthority, setPreviousWeaponLicencingAuthority] = useState<
    string[]
  >([]);
  const { t } = useTranslations();
  const { hasPermissions } = usePermission();
  const { loadDictionaries } = useUsers();
  const { phoneCode, legalEntityPrefix, organisationCategory, weaponLicencingAuthority } =
    useDictionary();
  const { longDateFormat, getProperties } = useGlobalProperty();
  const { hasWeaponLicenceModule } = useWeaponLicenceModule();

  const parsedPhoneCodeDictionary: IParsedDictionary = parseDictionary(phoneCode, 'name');

  useEffect(() => {
    if (auditLog?.previousObjectValue) {
      (async () => {
        const previousObjectValue = auditLog?.previousObjectValue as any;
        const previousUserData: IUser = {
          active: previousObjectValue.active || '',
          assignedLegalIds: previousObjectValue.assignedLegalIds || [],
          blocked: previousObjectValue.blocked || '',
          created: previousObjectValue.created || '',
          email: previousObjectValue.email || '',
          firstName: previousObjectValue.firstName || '',
          lastLoginDate: previousObjectValue.lastLoginDate || '',
          lastName: previousObjectValue.lastName || '',
          password: '',
          phoneNumber: previousObjectValue.phoneNumber || {
            codeDictionaryUniqueId: '',
            value: '',
          },
          prefixGroupIds: previousObjectValue.prefixGroupIds || [],
          licenceAuthorityIds: previousObjectValue.licenceAuthorityIds || [],
          organisationCategoryIds: previousObjectValue.organisationCategoryIds || [],
          removed: previousObjectValue.removed || '',
          roleIds: previousObjectValue.roleIds || [],
          status: previousObjectValue.status || '',
          trustedDeviceIds: previousObjectValue.trustedDeviceIds || [],
          version: previousObjectValue.version || 0,
          uniqueId: '',
          displayUniqueId: previousObjectValue.displayUniqueId,
        };
        setPreviousUserDetails(previousUserData);
        if (previousObjectValue.roleIds && previousObjectValue.roleIds.length) {
          const responseRoles = await getRoles(1, 100, {}, null);
          if (responseRoles) {
            const roles = responseRoles.content
              .map((role) => previousObjectValue.roleIds.includes(role.uniqueId) && role.name)
              .filter((row) => !!row);
            setPreviousRoleNames(roles as string[]);
          }
        }
        if (previousObjectValue.prefixGroupIds && previousObjectValue.prefixGroupIds.length) {
          if (legalEntityPrefix) {
            const names = legalEntityPrefix
              .map(
                (prefix) =>
                  previousObjectValue.prefixGroupIds.includes(prefix.uniqueId.toString()) &&
                  prefix.name,
              )
              .filter((row) => !!row);
            setPreviousPrefixNames(names as string[]);
          }
        }
        if (
          previousObjectValue.organisationCategoryIds &&
          previousObjectValue.organisationCategoryIds.length
        ) {
          if (organisationCategory) {
            const names = organisationCategory
              .map(
                (category) =>
                  previousObjectValue.organisationCategoryIds.includes(
                    category.uniqueId.toString(),
                  ) && category.name,
              )
              .filter((row) => !!row);
            setPreviousOrgCategoryNames(names as string[]);
          }
        }
        if (
          previousObjectValue.licenceAuthorityIds &&
          previousObjectValue.licenceAuthorityIds.length
        ) {
          if (weaponLicencingAuthority) {
            const names = weaponLicencingAuthority
              .map(
                (item) =>
                  previousObjectValue.licenceAuthorityIds.includes(item.uniqueId.toString()) &&
                  item.name,
              )
              .filter((row) => !!row);
            setPreviousWeaponLicencingAuthority(names as string[]);
          }
        }
      })();
    }

    if (auditLog?.postObjectValue && hasPermissions([EPermission.MANAGE_USERS])) {
      (async () => {
        const postObjectValue = auditLog?.postObjectValue as any;
        if (postObjectValue) {
          setUserDetails(postObjectValue);
          if (postObjectValue.roleIds && postObjectValue.roleIds.length) {
            const responseRoles = await getRoles(1, 100, {}, null);
            if (responseRoles) {
              const roles = responseRoles.content
                .map((role) => postObjectValue.roleIds.includes(role.uniqueId) && role.name)
                .filter((row) => !!row);
              setRoleNames(roles as string[]);
            }
          }
          if (postObjectValue.prefixGroupIds && postObjectValue.prefixGroupIds.length) {
            if (legalEntityPrefix) {
              const names = legalEntityPrefix
                .map(
                  (prefix) =>
                    postObjectValue.prefixGroupIds.includes(prefix.uniqueId.toString()) &&
                    prefix.name,
                )
                .filter((row) => !!row);
              setPrefixNames(names as string[]);
            }
          }
          if (
            postObjectValue.organisationCategoryIds &&
            postObjectValue.organisationCategoryIds.length
          ) {
            if (organisationCategory) {
              const names = organisationCategory
                .map(
                  (category) =>
                    postObjectValue.organisationCategoryIds.includes(
                      category.uniqueId.toString(),
                    ) && category.name,
                )
                .filter((row) => !!row);
              setOrgCategoryNames(names as string[]);
            }
          }
          if (
            postObjectValue.licenceAuthorityIds &&
            postObjectValue.licenceAuthorityIds.length
          ) {
            if (weaponLicencingAuthority) {
              const names = weaponLicencingAuthority
                .map(
                  (item) =>
                    postObjectValue.licenceAuthorityIds.includes(item.uniqueId.toString()) &&
                    item.name,
                )
                .filter((row) => !!row);
              setWeaponLicencingAuthorityNames(names as string[]);
            }
          }
        }
      })();
    }
  }, [auditLog, legalEntityPrefix, organisationCategory, weaponLicencingAuthority]);

  useEffect(() => {
    getProperties();
    loadDictionaries();
  }, []);

  const statusChip = (userDetailsData: IUser | null | undefined) => {
    if (userDetailsData) {
      if (userDetailsData.removed) {
        return <Chips text={t('users.removed.label')} variant={EChipsVariants.error} />;
      }
      if (userDetailsData.blocked) {
        return <Chips text={t('users.blocked.label')} variant={EChipsVariants.warning} />;
      }
      return (
        <Chips
          text={userDetailsData.active ? t('users.active.label') : t('users.inactive.label')}
          variant={userDetailsData.active ? EChipsVariants.success : EChipsVariants.error}
        />
      );
    }
    return '-';
  };

  return (
    <>
      {previousUserDetails && (
        <>
          <AuditLogEventName>
            <Typography variant="h5">
              {auditLog?.eventNameUniqueId !== EAuditLogEvents.ADD_USER
                ? `${t('user.breadcrumbs.userDetails')} ${t(
                  'auditLogDetails.before.event.title',
                )}`
                : `${t('user.breadcrumbs.userDetails')} ${t(
                  'auditLogDetails.after.event.title',
                )}`}
            </Typography>
          </AuditLogEventName>
          <DetailsTemplate
            pageType={EDetailsPageTypes.auditLogView}
            title={`${previousUserDetails.firstName} ${previousUserDetails.lastName}`}
            disableActions={true}
          >
            <Grid size={{ xs: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('userDetails.displayUniqueId.label')}
                </Typography>
                <Typography variant="body2">{previousUserDetails.displayUniqueId}</Typography>
              </Stack>
            </Grid>
            <Grid size={{ xs: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('userDetails.firstName.label')}
                </Typography>
                <Typography variant="body2">{previousUserDetails.firstName}</Typography>
              </Stack>
            </Grid>
            <Grid size={{ xs: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('userDetails.lastName.label')}
                </Typography>
                <Typography variant="body2">{previousUserDetails.lastName}</Typography>
              </Stack>
            </Grid>
            <Grid size={{ xs: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('userDetails.phoneNumber.label')}
                </Typography>
                <Typography variant="body2">{`${previousUserDetails.phoneNumber &&
                  parsedPhoneCodeDictionary?.[
                  previousUserDetails.phoneNumber.codeDictionaryUniqueId
                  ]
                  ? `${parsedPhoneCodeDictionary?.[
                  previousUserDetails.phoneNumber.codeDictionaryUniqueId
                  ]
                  }${previousUserDetails.phoneNumber.value}`
                  : '-'
                  }`}</Typography>
              </Stack>
            </Grid>
            <Grid size={{ xs: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('userDetails.lastLogged.label')}
                </Typography>
                <Typography variant="body2">
                  {!previousUserDetails.removed
                    ? formatDate(previousUserDetails.lastLoginDate, longDateFormat)
                    : '-'}
                </Typography>
              </Stack>
            </Grid>
            <Grid size={{ xs: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('userDetails.role.label')}
                </Typography>
                <div>
                  {previousRoleNames.length > 0
                    ? previousRoleNames.map((roleItem) => (
                      <Chips key={roleItem} text={roleItem} variant={EChipsVariants.primary} />
                    ))
                    : '-'}
                </div>
              </Stack>
            </Grid>
            <Grid size={{ xs: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('userDetails.status.label')}
                </Typography>
                <div>{statusChip(previousUserDetails)}</div>
              </Stack>
            </Grid>
            <Grid size={{ xs: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('userDetails.prefix.label')}
                </Typography>
                <div>
                  {[...new Set([...previousPrefixNames, ...previousOrgCategoryNames])].map(
                    (item) => (
                      <Chips key={item} text={item} variant={EChipsVariants.primary} />
                    ),
                  )}
                </div>
              </Stack>
            </Grid>
            {hasWeaponLicenceModule() && (
              <Grid size={{ xs: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t('editUser.weaponLicencingAuthority.label')}
                  </Typography>
                  <div>
                    {previousWeaponLicencingAuthority.length > 1 ? (
                      <Chips text={t('general.chip.all')} variant={EChipsVariants.primary} />
                    ) : (
                      previousWeaponLicencingAuthority.map((item) => (
                        <Chips key={item} text={item} variant={EChipsVariants.primary} />
                      ))
                    )}
                  </div>
                </Stack>
              </Grid>
            )}
          </DetailsTemplate>
        </>
      )}

      {userDetails && auditLog?.eventNameUniqueId !== EAuditLogEvents.DELETE_USER && (
        <>
          <AuditLogEventName sx={{ marginTop: '20px' }}>
            <Typography variant="h5">
              {`${t('user.breadcrumbs.userDetails')} ${t('auditLogDetails.post.event.title')}`}
            </Typography>
          </AuditLogEventName>
          <DetailsTemplate
            pageType={EDetailsPageTypes.auditLogView}
            title={`${userDetails.firstName} ${userDetails.lastName}`}
            redirectToView={() =>
              navigate(
                `${ERouteLinks.ViewUser.replace(':id', userDetails.uniqueId)}?type=individual`,
              )
            }
            disableActions={userDetails.removed}
          >
            <Grid size={{ xs: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('userDetails.displayUniqueId.label')}
                </Typography>
                <Typography variant="body2">{userDetails.displayUniqueId}</Typography>
              </Stack>
            </Grid>
            <Grid size={{ xs: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('userDetails.firstName.label')}
                </Typography>
                <Typography variant="body2">{userDetails.firstName}</Typography>
              </Stack>
            </Grid>
            <Grid size={{ xs: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('userDetails.lastName.label')}
                </Typography>
                <Typography variant="body2">{userDetails.lastName}</Typography>
              </Stack>
            </Grid>
            <Grid size={{ xs: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('userDetails.phoneNumber.label')}
                </Typography>
                <Typography variant="body2">{`${userDetails.phoneNumber &&
                  parsedPhoneCodeDictionary?.[userDetails.phoneNumber.codeDictionaryUniqueId]
                  ? `${parsedPhoneCodeDictionary?.[
                  userDetails.phoneNumber.codeDictionaryUniqueId
                  ]
                  }${userDetails.phoneNumber.value}`
                  : '-'
                  }`}</Typography>
              </Stack>
            </Grid>
            <Grid size={{ xs: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('userDetails.lastLogged.label')}
                </Typography>
                <Typography variant="body2">
                  {!userDetails.removed
                    ? formatDate(userDetails.lastLoginDate, longDateFormat)
                    : '-'}
                </Typography>
              </Stack>
            </Grid>
            <Grid size={{ xs: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('userDetails.role.label')}
                </Typography>
                <div>
                  {roleNames.length
                    ? roleNames.map((roleItem) => (
                      <Chips key={roleItem} text={roleItem} variant={EChipsVariants.primary} />
                    ))
                    : '-'}
                </div>
              </Stack>
            </Grid>
            <Grid size={{ xs: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('userDetails.status.label')}
                </Typography>
                <div>{statusChip(userDetails)}</div>
              </Stack>
            </Grid>

            <Grid size={{ xs: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('userDetails.prefix.label')}
                </Typography>
                <div>
                  {[...new Set([...prefixNames, ...orgCategoryNames])].map((item) => (
                    <Chips key={item} text={item} variant={EChipsVariants.primary} />
                  ))}
                </div>
              </Stack>
            </Grid>
            {hasWeaponLicenceModule() && (

              <Grid size={{ xs: 6, lg: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t('editUser.weaponLicencingAuthority.label')}
                  </Typography>
                  <div>
                    {weaponLicencingAuthorityNames.length > 1 ? (
                      <Chips text={t('general.chip.all')} variant={EChipsVariants.primary} />
                    ) : (
                      weaponLicencingAuthorityNames.map((item) => (
                        <Chips key={item} text={item} variant={EChipsVariants.primary} />
                      ))
                    )}
                  </div>
                </Stack>
              </Grid>
            )}
          </DetailsTemplate>
        </>
      )}
    </>
  );
};

export default AdministrationUserEventDetails;
